import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatRM } from "@/lib/api";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, ShoppingBag, Clock3, Users, Star } from "lucide-react";

export default function AdminAnalytics() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/overview").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <div className="p-10 text-[#4a6e77]">{t("common.loading")}</div>;

  const cards = [
    { label: t("analytics.revenue"), value: formatRM(data.cards.total_revenue), Icon: TrendingUp, primary: true },
    { label: t("analytics.orders"), value: data.cards.orders_count, Icon: ShoppingBag },
    { label: t("analytics.pending"), value: data.cards.pending_orders, Icon: Clock3 },
    { label: t("analytics.members"), value: data.cards.members_count, Icon: Users },
    { label: t("analytics.agents"), value: data.cards.agents_count, Icon: Star },
  ];

  return (
    <div className="p-6 md:p-10" data-testid="admin-analytics-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("analytics.title")}</h1>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-cards">
        {cards.map((c, i) => (
          <div key={i} className={`rounded-lg p-5 border ${c.primary ? "bg-[#005870] text-white border-[#005870]" : "bg-white border-[#e5ebeb]"}`} data-testid={`analytics-card-${i}`}>
            <div className={`flex items-center gap-2 text-xs uppercase tracking-widest ${c.primary ? "text-white/70" : "text-[#4a6e77]"}`}>
              <c.Icon size={14} /> {c.label}
            </div>
            <div className={`mt-2 text-2xl font-semibold ${c.primary ? "" : "text-[#005870]"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="chart-monthly-revenue">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("analytics.monthlyRevenue")}</div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ebeb" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4a6e77" }} />
                <YAxis tick={{ fontSize: 11, fill: "#4a6e77" }} />
                <Tooltip formatter={(v) => formatRM(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#005870" strokeWidth={2} dot={{ fill: "#00868e" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="chart-category-revenue">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("analytics.categoryRevenue")}</div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.category_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ebeb" />
                <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#4a6e77" }} />
                <YAxis tick={{ fontSize: 11, fill: "#4a6e77" }} />
                <Tooltip formatter={(v) => formatRM(v)} />
                <Bar dataKey="revenue" fill="#00868e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="top-products">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("analytics.topProducts")}</div>
          <ul className="mt-3 divide-y divide-[#e5ebeb]">
            {data.top_products.map((p, i) => (
              <li key={p.id} className="py-2 flex justify-between text-sm">
                <span className="text-[#003645] truncate flex-1"><span className="text-[#00868e] font-semibold mr-2">{i + 1}</span>{p.name_zh}</span>
                <span className="text-[#005870] font-medium">{formatRM(p.revenue)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="top-agents">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("analytics.topAgents")}</div>
          <ul className="mt-3 divide-y divide-[#e5ebeb]">
            {data.top_agents_month.length === 0 && <li className="py-3 text-[#4a6e77] text-sm">{t("common.empty")}</li>}
            {data.top_agents_month.map((a, i) => (
              <li key={a.id} className="py-2 flex justify-between text-sm">
                <span className="text-[#003645] truncate flex-1"><span className="text-[#00868e] font-semibold mr-2">{i + 1}</span>{a.name}</span>
                <span className="text-[#005870] font-medium">{formatRM(a.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="recent-orders">
        <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("analytics.recent")}</div>
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-xs text-[#4a6e77]"><th className="py-2">Order</th><th>Customer</th><th>Total</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {data.recent_orders.map((o) => (
              <tr key={o.id}><td className="py-2 border-t border-[#e5ebeb]">#{o.order_no}</td><td className="border-t border-[#e5ebeb] text-[#4a6e77]">{o.user_name}</td><td className="border-t border-[#e5ebeb] text-[#005870] font-medium">{formatRM(o.cash_paid || o.total)}</td><td className="border-t border-[#e5ebeb] text-xs">{o.status}</td><td className="border-t border-[#e5ebeb] text-xs text-[#4a6e77]">{new Date(o.created_at).toLocaleDateString()}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
